// externals
import type { FC, PropsWithChildren } from 'react';
import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { Box } from '@interstate/components/Box';
import { Badge } from '@interstate/components/Badge';
import { Action } from '@interstate/components/Action';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { ArrowTopRightOnSquareIcon, ArrowsRightLeftIcon, PencilSquareIcon } from '@interstate/components/Icons';
import * as configSelectors from '@makemydeal/dr-dash-store/selectors/config';
import * as navigationActionCreators from '@makemydeal/dr-dash-store/actions/navigationActionCreators';
import * as dealershipsSelectors from '@makemydeal/dr-dash-store/selectors/dealershipsSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { getNumberOfDaysStartingFromTargetDate } from '@makemydeal/dr-common-utils';
import { joinNonEmptyStringParts, normalizeStringPart } from '@makemydeal/dr-dash-ui-utils';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { dealLockHooks } from '../../hooks';

// components
import { LinkContainer, TrimStockEditCard } from './Style';
import { FormContext } from '../formManager/formContext';
import { FormStockInput } from '../formManager/FormStockInput';
import { FormTextInput } from '../formManager/FormTextInput';

type DrawerHeaderProps = PropsWithChildren<{
    condition: string;
    inventoryDate: string;
    year: string;
    model: string;
    make: string;
    trim: string;
    vin: string;
    stockNumber: string;
}>;

export const DrawerHeader: FC<DrawerHeaderProps> = ({ condition, inventoryDate, year, model, make, trim, vin, stockNumber }) => {
    const { isStockEditable, handleClickEditStock, isTrimEditable, handleClickEditTrim, hideDrawer } = useContext(FormContext);
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const vAutoUrl = useSelector(configSelectors.getVAutoInventoryUrl);
    const isSwitchVehicleEnabled = useSelector(dealerSelectors.isSwitchVehicleEnabled);
    const isHideChangeVehicleNoDealerships = useSelector(featureToggleSelectors.isHideChangeVehicleNoDealershipsEnabled);
    const dealerships = useSelector(dealershipsSelectors.getDealerships);
    const shouldHideChangeVehicle = isHideChangeVehicleNoDealerships && dealerships.length === 0;
    const isTrimAndStockEditEnabled = useSelector(featureToggleSelectors.isTrimAndStockEditEnabled);
    const stockEditMode: 'legacy-location' | 'new-location' = isTrimAndStockEditEnabled ? 'new-location' : 'legacy-location';
    const age = inventoryDate && getNumberOfDaysStartingFromTargetDate(inventoryDate);
    const isLocked = dealLockHooks.useIsDealLocked();
    const hasVehicle = useSelector(vehicleSelectors.getHasVehicle);

    const stockDisplayWithEdit = (displayValue: string) => (
        <Box display="flex" alignItems="flex-start" gap={0.5} mt={1} pb={0.5}>
            <Typography data-testid="drawer-header-stock" variant="body-md" sx={{ wordBreak: 'break-all' }}>
                Stock # {displayValue}
            </Typography>
            <Action
                actionIcon={{
                    icon: <PencilSquareIcon />,
                    start: true
                }}
                onClick={handleClickEditStock}
                data-testid="edit-stock"
                disabled={isLocked}
            />
        </Box>
    );

    const renderStockSection = () => {
        return isStockEditable ? <FormStockInput name="stockNumber" label="Stock #" /> : stockDisplayWithEdit(stockNumber || '');
    };

    const titleTrim = normalizeStringPart(trim) || (isTrimEditable ? '' : '\u2014');
    const titleText = joinNonEmptyStringParts(normalizeStringPart(condition).toUpperCase(), year, make, model, titleTrim);

    return (
        <Box paddingBottom={4} display="flex" gap={2} alignItems="flex-start">
            <Box flex={1} minWidth={0}>
                <Box display="flex" marginBottom={1.5} justifyContent="space-between" gap={1}>
                    <Typography data-testid="drawer-header-title" variant="strong-md">
                        {titleText}
                    </Typography>
                    {stockEditMode === 'new-location' && !isTrimEditable && (
                        <Action
                            actionIcon={{ icon: <PencilSquareIcon />, start: true }}
                            onClick={handleClickEditTrim}
                            data-testid="edit-trim"
                            size="sm"
                            disabled={isLocked}
                        >
                            Edit Trim & Stock#
                        </Action>
                    )}
                    {!!age && age > 0 && (
                        <Box>
                            <Badge variant="transparent" data-testid="drawer-header-age">
                                Age: {age}d
                            </Badge>
                        </Box>
                    )}
                </Box>
                <Box marginBottom={1.5} color="white">
                    <Typography data-testid="drawer-header-vin" variant="body-md">
                        VIN {vin}
                    </Typography>
                    {stockEditMode !== 'new-location' && renderStockSection()}
                </Box>
                <LinkContainer>
                    {isSwitchVehicleEnabled && !shouldHideChangeVehicle && (
                        <Action
                            onClick={() => {
                                hideDrawer();
                                dispatch(navigationActionCreators.navigateToChangeVehicle());
                            }}
                            size="xs"
                            data-testid="btnChangeVehicle"
                            actionIcon={{
                                icon: <ArrowsRightLeftIcon />,
                                start: true
                            }}
                        >
                            Change Vehicle
                        </Action>
                    )}
                    {vAutoUrl && (
                        <Action
                            data-testid="vauto-link"
                            size="xs"
                            onClick={() => {
                                window.open(`${vAutoUrl}&vin=${vin}`, '_blank', 'noopener,noreferrer');
                                hideDrawer();
                            }}
                            disabled={!hasVehicle}
                            actionIcon={{
                                icon: <ArrowTopRightOnSquareIcon />,
                                start: true
                            }}
                        >
                            vAuto
                        </Action>
                    )}
                </LinkContainer>
            </Box>
            {isTrimEditable && (
                <TrimStockEditCard theme={theme} data-testid="trim-stock-edit-card">
                    <FormTextInput name="trim" label="Trim" />
                    <FormStockInput name="stockNumber" label="Stock#" />
                </TrimStockEditCard>
            )}
        </Box>
    );
};
